import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useLearner } from '../../hooks/useLearner';
import { applyPlacement } from '../../api/learners';
import ProgressRing from '../../components/delight/ProgressRing';
import useFocusOnMount from '../../hooks/useFocusOnMount';
import s from './PlacementReviewPage.module.css';

export default function PlacementReviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const mainRef = useFocusOnMount();

  const { data: activeLearner } = useLearner();

  const story = location.state?.story;
  const placementBand = location.state?.placementBand;
  const learnerId = location.state?.learnerId || activeLearner?.id;

  // FP-EVAL-IN-CURRICULUM-01: require in_curriculum so out-of-sequence chars
  // (e.g. title chars like 奇/境 absent from sagebooks_500's sequence) never
  // appear as tappable cards. Tapping one otherwise produces 0/501 because the
  // placement route has no curriculum position for it to anchor the frontier.
  const evalCharacters = useMemo(() => {
    if (!Array.isArray(story?.characters)) return [];
    return story.characters.filter(
      (c) => Number.isInteger(c?.id) && c.in_curriculum === true,
    );
  }, [story]);

  const [phase, setPhase] = useState('marking');
  const [knownSet, setKnownSet] = useState(() => new Set());
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [inferredKnownCount, setInferredKnownCount] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);

  useEffect(() => {
    if (!story || !placementBand || !learnerId) {
      navigate('/', { replace: true });
    }
  }, [story, placementBand, learnerId, navigate]);

  if (!story || !placementBand || !learnerId) {
    return null;
  }

  function toggleKnown(charId) {
    setKnownSet((prev) => {
      const next = new Set(prev);
      if (next.has(charId)) next.delete(charId);
      else next.add(charId);
      return next;
    });
  }

  function handleSubmit() {
    setError(null);
    if (knownSet.size === 0) {
      setPhase('nothing_toast');
      return;
    }
    submitPlacement({ knownIds: [...knownSet] });
  }

  async function submitPlacement({ knownIds, confirmed, justThese }) {
    setPhase('submitting');
    setError(null);
    try {
      const options = {
        band: placementBand,
        knownCharacterIds: knownIds,
        evalCharacterIds: evalCharacters.map((c) => c.id),
      };
      if (confirmed) options.confirmed = true;
      if (justThese) options.justThese = true;
      const result = await applyPlacement(learnerId, options);

      if (result?.requiresConfirmation) {
        setInferredKnownCount(result.inferredKnownCount ?? 0);
        setPhase('safety_toast');
        return;
      }

      setSummary({
        knownCount: result?.knownCount ?? 0,
        totalCount: result?.totalCount ?? 0,
      });
      queryClient.invalidateQueries({ queryKey: ['learnerProfile'] });
      // FP-EVAL-CACHE-01: placement just stamped placed_at server-side, which
      // flips isOnboardingComplete → true. Invalidate ['me'] so OnboardingGuard
      // re-reads the new onboarding_complete value, and ['onboarding','resume']
      // so the stale resume_eval payload from when the user entered the reader
      // can't be reused on the next guard render.
      queryClient.invalidateQueries({ queryKey: ['me'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'resume'] });
      // FP-LEARNER-STORE-RETIREMENT-01: refresh the ['learners'] query so
      // placement_band is fresh on the dashboard via useLearner().
      queryClient.invalidateQueries({ queryKey: ['learners'] });
      setPhase('summary');
    } catch (err) {
      setError(err?.message || t('placementReview.errorGeneric'));
      setPhase('marking');
    }
  }

  function handleNothingConfirm() {
    submitPlacement({ knownIds: [] });
  }

  function handleNothingBack() {
    setPhase('marking');
  }

  function handleSafetyAccept() {
    submitPlacement({ knownIds: [...knownSet], confirmed: true });
  }

  function handleSafetyJustThese() {
    submitPlacement({ knownIds: [...knownSet], justThese: true });
  }

  function handleSafetyBack() {
    setInferredKnownCount(0);
    setPhase('marking');
  }

  async function handleSummaryCta() {
    setIsNavigating(true);
    try {
      // refetchQueries awaits the actual fetch — guarantees onboarding_complete
      // is fresh in cache before OnboardingGuard evaluates it on the next route.
      // invalidateQueries alone only marks stale; with no active subscriber here,
      // the background refetch never fires, causing a false 'onboarding_complete:false'
      // flash on the first guard render. (FP-PLACEMENT-FLICKER-01)
      await queryClient.refetchQueries({ queryKey: ['me'] });
      await queryClient.refetchQueries({ queryKey: ['onboarding', 'resume'] });
    } catch { /* non-critical — navigate regardless */ }
    queryClient.invalidateQueries({ queryKey: ['learnerProfile'] });
    navigate('/', { replace: true });
  }

  return (
    <main className={s.page} ref={mainRef} tabIndex={-1}>
      {phase === 'marking' && (
        <div className={s.shell}>
          <div className={s.markingHeader}>
            <h1 className={s.title}>{t('placementReview.title')}</h1>
            <p className={s.subtitle}>{t('placementReview.subtitle')}</p>
          </div>

          <div className={s.grid} role="list">
            {evalCharacters.map((c) => {
              const isSelected = knownSet.has(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  role="listitem"
                  className={`${s.card} ${isSelected ? s.cardSelected : ''}`}
                  onClick={() => toggleKnown(c.id)}
                  aria-pressed={isSelected}
                >
                  <span className={s.cardChar}>{c.char}</span>
                  {c.pinyin && <span className={s.cardPinyin}>{c.pinyin}</span>}
                  {c.meaning && <span className={s.cardMeaning}>{c.meaning}</span>}
                </button>
              );
            })}
          </div>

          {error && <p className={s.error} role="alert">{error}</p>}

          <div className={s.stickyFooter}>
            <button type="button" className={s.btnPrimary} onClick={handleSubmit}>
              {t('placementReview.submitCta')}
            </button>
          </div>
        </div>
      )}

      {phase === 'nothing_toast' && (
        <div className={s.shellCentered}>
          <div className={s.copyBlock}>
            <h2 className={s.headline}>
              {t('placementReview.nothingToast.headline')}
            </h2>
            <p className={s.body}>
              {t('placementReview.nothingToast.body', { name: activeLearner?.name || '' })}
            </p>
          </div>
          <div className={s.btnStack}>
            <button type="button" className={s.btnPrimary} onClick={handleNothingConfirm}>
              {t('placementReview.nothingToast.confirm')}
            </button>
            <button type="button" className={s.btnGhost} onClick={handleNothingBack}>
              {t('placementReview.nothingToast.back')}
            </button>
          </div>
        </div>
      )}

      {phase === 'safety_toast' && (
        <div className={s.shellCentered}>
          <div className={s.copyBlock}>
            <h2 className={s.headline}>
              {t('placementReview.safetyToast.headline')}
            </h2>
            <p className={s.body}>
              {t('placementReview.safetyToast.body', { count: inferredKnownCount })}
            </p>
          </div>
          <div className={s.btnStack}>
            <button type="button" className={s.btnPrimary} onClick={handleSafetyAccept}>
              {t('placementReview.safetyToast.accept', { count: inferredKnownCount })}
            </button>
            <button type="button" className={s.btnPrimary} onClick={handleSafetyJustThese}>
              {t('placementReview.safetyToast.justThese')}
            </button>
            <button type="button" className={s.btnGhost} onClick={handleSafetyBack}>
              {t('placementReview.safetyToast.back')}
            </button>
          </div>
        </div>
      )}

      {phase === 'submitting' && (
        <div className={s.shellCentered}>
          <p className={s.body}>{t('placementReview.submitting')}</p>
        </div>
      )}

      {phase === 'summary' && summary && (
        <div className={s.summaryCard}>
          <div className={s.summaryTop}>
            <img
              src={`/assets/onboarding/${activeLearner?.buddy?.slug || 'ling'}.webp`}
              alt=""
              aria-hidden="true"
              className={s.summaryBuddy}
            />
            <div className={s.summaryRingWrap}>
              <ProgressRing
                known={summary.knownCount}
                previousThreshold={0}
                threshold={Math.max(summary.totalCount, summary.knownCount, 1)}
                size={100}
                centerContent={<span className={s.knownNum}>{summary.knownCount}</span>}
              />
              <p className={s.knownLabel}>
                {t('placementReview.summary.ringLabel', {
                  known: summary.knownCount,
                  total: summary.totalCount,
                })}
              </p>
            </div>
          </div>

          <div className={s.summaryBottom}>
            <div className={s.copyBlock}>
              <h2 className={s.headline}>{t('placementReview.summary.headline')}</h2>
              <p className={s.body}>
                {t('placementReview.summary.body', {
                  band: t(`switchCurriculum.band.${placementBand}`),
                })}
              </p>
            </div>
            <button type="button" className={s.btnPrimary} onClick={handleSummaryCta} disabled={isNavigating}>
              {isNavigating
                ? t('placementReview.summary.loading')
                : t('placementReview.summary.cta')}
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
